import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getMyDashboards } from "../api/client";

/**
 * Gate for /admin and /admin/retake.
 *
 * Requires BOTH:
 *   1. role = admin (or legacy is_admin flag)
 *   2. the "admin" dashboard key isn't explicitly denied for this user
 *
 * The second check means an admin can lock themselves (or another admin) out
 * of the admin panel via an explicit deny on the access control page. The
 * access control page itself is gated separately by AccessControlRoute,
 * which is role-only so admins can always unlock themselves.
 */
export default function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const [access, setAccess] = useState({ status: "loading", dashboards: [] });

  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    const load = async () => {
      try {
        const res = await getMyDashboards();
        if (!cancelled) {
          setAccess({
            status: "ready",
            dashboards: res.data?.dashboards || [],
          });
        }
      } catch (e) {
        if (!cancelled) setAccess({ status: "error", dashboards: [] });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading || access.status === "loading") {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const isAdmin = !!(user.is_admin || user.role === "admin");
  if (!isAdmin) return <Navigate to="/welcome" replace />;

  if (!access.dashboards.includes("admin")) {
    return <Navigate to="/welcome" replace />;
  }

  return children;
}
